<template>
  <div style="margin-top: 2rem;">

    <section id="campaignData"></section>

    <!-- Section header -->
    <div style="display: flex; align-items: center; gap: 0.75rem; margin-bottom: 1.25rem;">
      <div style="width: 2.5rem; height: 2.5rem; border-radius: 0.625rem; background: linear-gradient(135deg, #EC4899, #DB2777); display: flex; align-items: center; justify-content: center; box-shadow: 0 2px 6px rgba(236, 72, 153, 0.3);">
        <svg xmlns="http://www.w3.org/2000/svg" style="width: 1.25rem; height: 1.25rem; color: white;" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
          <path stroke-linecap="round" stroke-linejoin="round" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
        </svg>
      </div>
      <div>
        <h2 id="campaignData2" style="font-size: 1.25rem; font-weight: 700; color: #111827; margin: 0;">Campaign Data</h2>
        <p style="font-size: 0.75rem; color: #9CA3AF; margin: 0.125rem 0 0;">Filter and explore campaign statistics</p>
      </div>
    </div>

    <!-- Filters card -->
    <div style="padding: 1rem 1.25rem; background: #FAFAFA; border: 1px solid #E5E7EB; border-radius: 0.625rem; margin-bottom: 1.25rem;">
      <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(180px, 1fr)); gap: 1rem;">

        <div>
          <label for="year-select" style="display: block; font-size: 0.75rem; font-weight: 600; color: #6B7280; text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: 0.375rem;">Year</label>
          <select
            id="year-select"
            v-model="filters.year"
            @change="handleYearChange"
            :disabled="isLoading"
            style="width: 100%; padding: 0.5rem 0.75rem; border: 1px solid #E5E7EB; border-radius: 0.5rem; font-size: 0.875rem; color: #374151; background: white; outline: none; cursor: pointer; appearance: auto;"
          >
            <option value="">Select year</option>
            <option v-for="year in years" :key="year" :value="year">{{ year }}</option>
          </select>
        </div>

        <div v-if="admin">
          <label for="foundation-select" style="display: block; font-size: 0.75rem; font-weight: 600; color: #6B7280; text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: 0.375rem;">Foundation Campaign</label>
          <select
            id="foundation-select"
            v-model="filters.foundationCampaign"
            @change="handleFoundationChange"
            :disabled="isLoading"
            style="width: 100%; padding: 0.5rem 0.75rem; border: 1px solid #E5E7EB; border-radius: 0.5rem; font-size: 0.875rem; color: #374151; background: white; outline: none; cursor: pointer; appearance: auto;"
          >
            <option value="">All foundations</option>
            <option v-for="campaign in foundationCampaigns" :key="campaign.id" :value="campaign.id">{{ campaign.name }}</option>
          </select>
        </div>

        <div>
          <label for="subcampaign-select" style="display: block; font-size: 0.75rem; font-weight: 600; color: #6B7280; text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: 0.375rem;">Campaign Level</label>
          <select
            id="subcampaign-select"
            v-model="filters.subCampaign"
            @change="handleFiltersChange"
            :disabled="isLoading"
            style="width: 100%; padding: 0.5rem 0.75rem; border: 1px solid #E5E7EB; border-radius: 0.5rem; font-size: 0.875rem; color: #374151; background: white; outline: none; cursor: pointer; appearance: auto;"
          >
            <option value="">All levels</option>
            <option v-for="campaign in subCampaigns" :key="campaign.id" :value="campaign.id">{{ campaign.name }}</option>
          </select>
        </div>

      </div>
    </div>

    <!-- Loading State -->
    <div v-if="isLoading" style="text-align: center; padding: 2.5rem; background: #F9FAFB; border: 1px solid #E5E7EB; border-radius: 0.625rem; margin-bottom: 1.25rem;">
      <div style="display: inline-block; width: 2rem; height: 2rem; border: 3px solid #E5E7EB; border-top-color: #EC4899; border-radius: 50%; animation: spin 0.8s linear infinite;"></div>
      <!-- Progress bar -->
      <div style="max-width: 20rem; margin: 1rem auto 0; height: 0.375rem; background: #E5E7EB; border-radius: 9999px; overflow: hidden;">
        <div :style="{ height: '100%', background: 'linear-gradient(90deg, #EC4899, #DB2777)', borderRadius: '9999px', width: loadingProgress + '%', transition: 'width 0.3s ease' }"></div>
      </div>
      <p v-if="!showLoadingMessage" style="margin-top: 0.75rem; font-size: 0.875rem; color: #9CA3AF;">Loading {{ filters.year }} data...</p>
      <p v-else style="margin-top: 0.75rem; font-size: 0.875rem; color: #9CA3AF;">
        Computing statistics for {{ filters.year }}...<br>
        <span style="font-size: 0.75rem;">This might take a few moments.</span>
      </p>
    </div>

    <!-- Error State -->
    <div v-if="error" style="padding: 0.875rem 1rem; background: #FEF2F2; border: 1px solid #FECACA; border-radius: 0.625rem; color: #991B1B; font-size: 0.875rem; margin-bottom: 1.25rem;">
      {{ error }}
    </div>

    <!-- Stats data -->
    <div :style="{ opacity: isLoading ? 0.5 : 1, transition: 'opacity 0.2s' }" v-for="(stats, year) in statsData" :key="year" style="margin-bottom: 1.5rem;">

      <!-- Financial Data -->
      <div style="margin-bottom: 1.25rem;">
        <div style="display: flex; align-items: center; gap: 0.5rem; margin-bottom: 0.75rem;">
          <div style="width: 0.375rem; height: 0.375rem; border-radius: 50%; background: #EC4899;"></div>
          <h4 v-if="admin" style="font-size: 0.8125rem; font-weight: 700; color: #374151; text-transform: uppercase; letter-spacing: 0.05em; margin: 0;">{{ year }} Financial Data</h4>
          <h4 v-else style="font-size: 0.8125rem; font-weight: 700; color: #374151; text-transform: uppercase; letter-spacing: 0.05em; margin: 0;">{{ year }} Fundraising Data</h4>
        </div>
        <div style="display: grid; grid-template-columns: repeat(auto-fill, minmax(210px, 1fr)); gap: 0.75rem;">
          <template v-for="item in stats" :key="item.name + '-finance'">
            <campaign-stats-item v-if="item?.show && item?.type === 'finance'" :item="item"></campaign-stats-item>
          </template>
        </div>
      </div>

      <!-- Parent Campaign Data (admin only) -->
      <div v-if="admin" style="margin-bottom: 1.25rem;">
        <div style="display: flex; align-items: center; gap: 0.5rem; margin-bottom: 0.75rem;">
          <div style="width: 0.375rem; height: 0.375rem; border-radius: 50%; background: #EC4899;"></div>
          <h4 style="font-size: 0.8125rem; font-weight: 700; color: #374151; text-transform: uppercase; letter-spacing: 0.05em; margin: 0;">{{ year }} Parent Campaign Data</h4>
        </div>
        <div style="display: grid; grid-template-columns: repeat(auto-fill, minmax(210px, 1fr)); gap: 0.75rem;">
          <template v-for="item in stats" :key="item.name + '-parent'">
            <campaign-stats-item v-if="item?.show && item?.type === 'parent'" :item="item"></campaign-stats-item>
          </template>
        </div>
      </div>

      <!-- Supporter Campaign Data (admin only) -->
      <div v-if="admin" style="margin-bottom: 1.25rem;">
        <div style="display: flex; align-items: center; gap: 0.5rem; margin-bottom: 0.75rem;">
          <div style="width: 0.375rem; height: 0.375rem; border-radius: 50%; background: #EC4899;"></div>
          <h4 style="font-size: 0.8125rem; font-weight: 700; color: #374151; text-transform: uppercase; letter-spacing: 0.05em; margin: 0;">{{ year }} Supporter Campaign Data</h4>
        </div>
        <div style="display: grid; grid-template-columns: repeat(auto-fill, minmax(210px, 1fr)); gap: 0.75rem;">
          <template v-for="item in stats" :key="item.name + '-supporter'">
            <campaign-stats-item v-if="item?.show && item?.type === 'supporter'" :item="item"></campaign-stats-item>
          </template>
        </div>
      </div>

      <!-- Access Data -->
      <div style="margin-bottom: 1.25rem;">
        <div style="display: flex; align-items: center; gap: 0.5rem; margin-bottom: 0.75rem;">
          <div style="width: 0.375rem; height: 0.375rem; border-radius: 50%; background: #EC4899;"></div>
          <h4 style="font-size: 0.8125rem; font-weight: 700; color: #374151; text-transform: uppercase; letter-spacing: 0.05em; margin: 0;">{{ year }} Access Data</h4>
        </div>
        <div style="display: grid; grid-template-columns: repeat(auto-fill, minmax(210px, 1fr)); gap: 0.75rem;">
          <template v-for="item in stats" :key="item.name + '-access'">
            <campaign-stats-item v-if="item?.show && item?.type === 'access'" :item="item"></campaign-stats-item>
          </template>
        </div>
      </div>

    </div>
  </div>
</template>

<script>
import CampaignStatsItem from "./CampaignStatsItem";
import debounce from 'lodash/debounce';

export default {
  components: {
    CampaignStatsItem,
  },
  data() {
    return {
      years: {},
      admin: false,
      foundationCampaigns: [],
      subCampaigns: [],
      statsData: {},
      isLoading: false,
      isLoadingFoundations: false,
      error: null,
      loadingProgress: 0,
      showLoadingMessage: false,
      loadingTimeout: null,
      currentProgressInterval: null,
      filters: {
        year: '',
        foundationCampaign: '',
        subCampaign: ''
      },
      foundationCache: new Map(),
      statsCache: new Map(),
      pendingRequests: new Map()
    }
  },
  created() {
    this.debouncedFetchData = debounce(this.fetchData, 100);
    this.loadInitialData();
  },
  methods: {
    async loadInitialData() {
      try {
        this.isLoading = true;
        this.startLoadingTimer();

        const response = await axios.get('/reports/campaign-stats-initial');
        this.years = response.data.years;
        this.filters.year = response.data.mostRecentYear;
        this.foundationCampaigns = response.data.foundationCampaigns;
        this.admin = response.data.admin;

        if (this.filters.year && this.foundationCampaigns.length) {
          this.foundationCache.set(this.filters.year, this.foundationCampaigns);
        }

        await this.fetchData();
      } catch (error) {
        this.error = 'Failed to load initial data. Please try again.';
        console.error('Initial data loading error:', error);
      } finally {
        this.isLoading = false;
        if (this.loadingTimeout) clearTimeout(this.loadingTimeout);
        if (this.currentProgressInterval) clearInterval(this.currentProgressInterval);
      }
    },

    async preloadAdjacentYears(currentYear) {
      const yearsArray = Object.values(this.years);
      const currentIndex = yearsArray.indexOf(currentYear);
      if (currentIndex === -1) return;

      const adjacentYears = [];
      if (currentIndex > 0) adjacentYears.push(yearsArray[currentIndex - 1]);
      if (currentIndex < yearsArray.length - 1) adjacentYears.push(yearsArray[currentIndex + 1]);

      adjacentYears.forEach(year => {
        const cacheKey = `${year}--`;
        if (!this.statsCache.has(cacheKey) && !this.pendingRequests.has(cacheKey)) {
          this.fetchYearData(year, true);
        }
      });
    },

    async fetchYearData(year, isPreload = false) {
      const cacheKey = `${year}--`;

      if (this.pendingRequests.has(cacheKey)) {
        return this.pendingRequests.get(cacheKey);
      }

      const token = document.head.querySelector('meta[name="csrf-token"]');
      const request = axios.post('/reports/campaign-stats-get', {
        selectedYear: year,
        loadFullStats: true,
        includeFoundations: true,
        isInitialLoad: !isPreload,
        requireFullData: !isPreload,
        _token: token ? token.content : null
      });

      this.pendingRequests.set(cacheKey, request);

      try {
        const response = await request;
        if (response.data.foundationCampaigns) {
          this.foundationCache.set(year, response.data.foundationCampaigns);
        }
        if (response.data.statsData) {
          this.statsCache.set(cacheKey, {
            statsData: response.data.statsData,
            subCampaigns: response.data.subCampaigns || []
          });
        }
      } catch (error) {
        if (!isPreload) throw error;
      } finally {
        this.pendingRequests.delete(cacheKey);
      }
    },

    startLoadingTimer() {
      if (this.currentProgressInterval) clearInterval(this.currentProgressInterval);
      if (this.loadingTimeout) clearTimeout(this.loadingTimeout);

      this.loadingProgress = 0;
      this.showLoadingMessage = false;

      this.loadingTimeout = setTimeout(() => {
        this.showLoadingMessage = true;
      }, 10000);

      let progressStep = 10;
      let currentProgress = 0;

      const progressInterval = setInterval(() => {
        if (!this.isLoading) {
          clearInterval(progressInterval);
          this.loadingProgress = 100;
          setTimeout(() => { this.loadingProgress = 0; }, 500);
          return;
        }
        if (currentProgress > 30) progressStep = 5;
        if (currentProgress > 60) progressStep = 2;
        if (currentProgress > 80) progressStep = 1;
        currentProgress = Math.min(90, currentProgress + progressStep);
        this.loadingProgress = currentProgress;
      }, 1000);

      this.currentProgressInterval = progressInterval;
    },

    beforeUnmount() {
      if (this.currentProgressInterval) clearInterval(this.currentProgressInterval);
      if (this.loadingTimeout) clearTimeout(this.loadingTimeout);
    },

    async handleYearChange() {
      this.error = null;
      const previousStatsData = { ...this.statsData };

      this.filters.foundationCampaign = '';
      this.filters.subCampaign = '';

      this.startLoadingTimer();
      this.isLoading = true;

      const cacheKey = this.getCacheKey();
      if (this.statsCache.has(cacheKey) && this.foundationCache.has(this.filters.year)) {
        const cachedStats = this.statsCache.get(cacheKey);
        this.statsData = cachedStats.statsData;
        this.subCampaigns = cachedStats.subCampaigns;
        this.foundationCampaigns = this.foundationCache.get(this.filters.year);
        this.isLoading = false;
        this.preloadAdjacentYears(this.filters.year);
        return;
      }

      try {
        await this.fetchYearData(this.filters.year);
        const cacheKey = this.getCacheKey();
        const cachedStats = this.statsCache.get(cacheKey);
        this.statsData = cachedStats.statsData;
        this.subCampaigns = cachedStats.subCampaigns;
        this.foundationCampaigns = this.foundationCache.get(this.filters.year);
        this.preloadAdjacentYears(this.filters.year);
      } catch (error) {
        this.error = 'Failed to load year data. Please try again.';
        console.error('Year change error:', error);
        this.statsData = previousStatsData;
      } finally {
        this.isLoading = false;
        if (this.loadingTimeout) clearTimeout(this.loadingTimeout);
      }
    },

    handleFoundationChange() {
      this.error = null;
      this.filters.subCampaign = '';
      this.debouncedFetchData();
    },

    handleFiltersChange() {
      this.error = null;
      this.debouncedFetchData();
    },

    getCacheKey() {
      return `${this.filters.year}-${this.filters.foundationCampaign}-${this.filters.subCampaign}`;
    },

    async fetchData(retryCount = 0) {
      if (!this.filters.year) return;

      const cacheKey = this.getCacheKey();
      if (this.statsCache.has(cacheKey)) {
        const cachedData = this.statsCache.get(cacheKey);
        this.statsData = cachedData.statsData;
        this.subCampaigns = cachedData.subCampaigns;
        this.isLoading = false;
        return;
      }

      try {
        this.isLoading = true;
        this.startLoadingTimer();

        const token = document.head.querySelector('meta[name="csrf-token"]');
        const response = await axios.post('/reports/campaign-stats-get', {
          selectedYear: this.filters.year,
          selectedFoundationCampaign: this.filters.foundationCampaign,
          selectedSubCampaign: this.filters.subCampaign,
          loadFullStats: true,
          debug: true,
          explain: true,
          _token: token ? token.content : null
        });

        // Handle async computation in progress
        if (response.data.status === 'computing') {
          if (retryCount < 3) {
            this.showLoadingMessage = true;
            const retryDelay = (response.data.retry_after || 3) * 1000;
            await new Promise(resolve => setTimeout(resolve, retryDelay));
            return this.fetchData(retryCount + 1);
          } else {
            this.error = 'Statistics are still being computed. Please try again in a moment.';
            this.isLoading = false;
            if (this.loadingTimeout) clearTimeout(this.loadingTimeout);
            if (this.currentProgressInterval) clearInterval(this.currentProgressInterval);
            return;
          }
        }

        if (response.data.statsData) {
          this.statsData = response.data.statsData;
          this.subCampaigns = response.data.subCampaigns || [];
          this.statsCache.set(cacheKey, {
            statsData: response.data.statsData,
            subCampaigns: response.data.subCampaigns || []
          });
        }

        if (response.data.debug) {
          console.log('Query Performance Data:', response.data.debug);
        }
      } catch (error) {
        this.error = 'Failed to fetch campaign stats. Please try again.';
        console.error('Data fetching error:', error);
      } finally {
        this.isLoading = false;
        if (this.loadingTimeout) clearTimeout(this.loadingTimeout);
        if (this.currentProgressInterval) clearInterval(this.currentProgressInterval);
      }
    },
  },
}
</script>

<style>
@keyframes spin {
  to { transform: rotate(360deg); }
}
</style>
